"use client";

import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useMemo, type CSSProperties } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useIsMobile } from "@/hooks/useIsMobile";
import type { RootState } from "@/store/store";
import {
  LEAD_DROP_FORM_SECTION_VARIANTS,
  LEAD_DROP_VARIANTS,
} from "../../../constants";

/**
 * Banner 5004 — KUK Marketing Banner.
 *
 * Port of kuk-campus-portal `components/organisms/MarketingBanner.tsx`: the
 * full-bleed CTA band the programme landing pages drop between sections. A
 * background photograph, a heading, one line of supporting copy and a single
 * button.
 *
 * ALSO THE LANDING-PAGE HERO. The same shape — full-bleed background, heading,
 * supporting line, one CTA — is what the reference hero is, at hero height. Two
 * fields exist for that role and are inert without them: `bleed_background_color`
 * paints the reference's black-30% scrim over the photo so white copy stays
 * legible, and `metadata_1` carries its accreditation cards (UGC Entitled /
 * NAAC A++ / AICTE Approved). Both default to off, so the marketing bands that
 * do not set them render exactly as before.
 *
 * NOT CARRIED OVER from the reference hero: its inline lead form. That is a
 * deliberate scope decision, not a gap in the widget.
 *
 * ONE VARIANT, NOT THREE. v1 is a `type` switch over three hardcoded blocks —
 * `career` ("Launch Your Career"), `emi` ("No cost EMI starting at ₹…") and
 * `get_in_touch` ("Ready to Take the Next Step?") — each with its own copy,
 * image URL, height and colours baked into the component. Every one of those
 * differences is content, so here they are CMS fields and the three v1 types
 * are three rows an editor configures. That is what lets the same band carry
 * different copy on different pages, which v1 cannot do at all.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see
 * globals.css `--breakpoint-*`), so every `md:` in the reference is ported as
 * `tab:`.
 *
 * LAYOUT PRESET — v1 pairs the copy's corner with the background's focal point:
 * the two left-aligned types focus the image at `70% top` (subject on the
 * right, clear of the copy) and the right-aligned one at `40% top`. They are
 * not independent choices, so one field drives both. `backvariant` selects the
 * pair — `variant_1` is v1's career/emi block (copy centred on the left, image
 * at 70%), `variant_2` is its get_in_touch block (copy in the bottom-right,
 * image at 40%). Splitting them into two fields would let an editor pick a
 * combination the reference never renders and the art was never cut for.
 *
 * CTA FILL — v1's three buttons are a white-bordered transparent one (career,
 * get_in_touch) and a gradient-filled one (emi). Rather than a mode enum, the
 * gradient pair is optional: set both `gradient_color_3` and `gradient_color_4`
 * and the button fills with the gradient, leave either empty and it falls back
 * to the flat `apply_now_color` plus `apply_now_border_color`. Both v1 looks
 * are reachable, and so is a plain solid button, with no extra switch.
 *
 * DROPPED — v1's two-tone EMI heading, where a regex digs the fee out of the
 * KeyFeatures copy elsewhere on the page and paints it in a second colour. The
 * amount is now simply part of the heading an editor types. A heading that
 * genuinely needs two colours belongs in the Rich Text Editor widget, not in a
 * banner that has to parse another section's prose to render itself.
 *
 * `isUserVerified` — v1 threads a cookie-derived flag through every section to
 * swap the CTA for a "Continue Application" link. This app resolves that
 * centrally through the leaddrop store, so the button here is a plain
 * CMS-configured CTA: `apply_now_is_cta` opens the lead drop, otherwise it
 * links to `apply_now_link`.
 */

/** v1: `h-[366px]` on the career band, the tallest of the three at mobile. */
const DEFAULT_HEIGHT = 366;
/** v1: `bg-[position]` — the focal point each MARKETING BAND layout pairs with. */
const FOCAL_POINT = {
  variant_1: "70% top",
  variant_2: "40% top",
} as const;

/**
 * The hero does NOT take its band's focal point. The reference bands offset the
 * art (70% / 40%) to keep their subject clear of the copy, but the hero uses
 * `object-position: top` — centred horizontally, anchored to the top — because
 * its copy sits over a scrim rather than beside the subject. Using the band
 * offset here pushes the subject off-centre.
 */
const HERO_FOCAL_POINT = "center top";

/**
 * Reference hero heights: a flat `60vh` below `tab`, and `75vh` above it.
 *
 * THE 75 IS THE POINT. v1 does not hard-code its desktop height — it reads the
 * window at runtime and picks `windowHeight < 800 ? "90vh" : "75vh"`. Its
 * server-rendered HTML therefore always serialises `90vh`, because
 * `windowHeight` is 0 before hydration. Taking the height from that markup gives
 * every normal desktop a hero ~20% taller than the live site actually shows.
 *
 * The short-window branch (90vh under 800px tall) is deliberately NOT carried
 * over. It is a JS measurement quirk that fires on a minority of viewports, and
 * expressing it needs a `max-height` media query — which Tailwind will not
 * generate from a stacked arbitrary variant, so it would compile to nothing and
 * silently do nothing. 75vh is the height desktop users see.
 */
const HERO_HEIGHT_MOBILE_CLASS = "h-[60vh]";
const HERO_HEIGHT_DESKTOP_CLASS = "tab:h-[75vh]";

type LayoutVariant = keyof typeof FOCAL_POINT;

interface Banner5004Data {
  status?: boolean | number | string;
  /** Background photograph. */
  image?: string;
  /** Optional; falls back to `image`. */
  mobile_image?: string;
  image_alt?: string;
  /** Painted behind the image, and alone when no image is set. */
  background_color?: string;
  /**
   * Scrim laid OVER the image. Empty on the marketing bands; the hero sets the
   * reference's `bg-[#000000] bg-opacity-30` so white copy stays readable.
   */
  bleed_background_color?: string;
  /** Reference hero: an inline lead form card pinned to the right. */
  show_lead_form?: boolean | number | string;
  enable_lead_form?: boolean | number | string;
  /** Band height from 768px up. */
  hero_section_height?: number;
  /** Copy corner + image focal point. See the LAYOUT PRESET note above. */
  backvariant?: string;
  title?: string;
  title_color?: string;
  description?: string;
  description_color?: string;
  /** CTA */
  apply_now_show?: boolean | number | string;
  apply_now_text?: string;
  apply_now_link?: string;
  apply_now_is_cta?: boolean | number | string;
  apply_now_color?: string;
  apply_now_border_color?: string;
  cta_text_color?: string;
  /** Optional gradient fill — both must be set for it to apply. */
  gradient_color_3?: string;
  gradient_color_4?: string;
  /** Accreditation cards (hero only). Empty on the marketing bands. */
  metadata_1?: AccreditationCard[];
  /** Card fill + label colour for those cards. */
  horizontal_stripe_color?: string;
  card_title_color?: string;
}

/** One accreditation card: a badge image beside a short label. */
interface AccreditationCard {
  id?: string | number;
  image?: string;
  alt_image?: string;
  title?: string;
  priority?: number;
  status?: boolean;
}

/**
 * A nullable CMS column, not a string: these come straight from MySQL, where an
 * unset URL is NULL — and a destructuring default only fills in `undefined`, so
 * `null` reaches here intact.
 */
function normalizeHref(href: string | null | undefined): string {
  const h = (href ?? "").trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

/** `url("…")` with the quote that would close it escaped out. */
function toCssUrl(src: string): string {
  return `url("${src.replace(/"/g, "%22")}")`;
}

const Banner5004 = (props: { data?: Banner5004Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as Banner5004Data | undefined;
  const dispatch = useDispatch();

  const {
    image = "",
    mobile_image = "",
    image_alt = "",
    background_color = "transparent",
    bleed_background_color = "",
    show_lead_form,
    enable_lead_form,
    horizontal_stripe_color = "rgba(255, 255, 255, 0.80)",
    card_title_color = "#101112",
    hero_section_height = DEFAULT_HEIGHT,
    backvariant = "variant_1",
    title = "",
    title_color = "#FFFFFF",
    description = "",
    description_color = "#FFFFFF",
    apply_now_text = "Get in touch",
    apply_now_link = "",
    apply_now_color = "transparent",
    apply_now_border_color = "#FFFFFF",
    cta_text_color = "#FFFFFF",
    gradient_color_3 = "",
    gradient_color_4 = "",
  } = sectionData || {};

  const accreditations = useMemo(() => {
    const rows = sectionData?.metadata_1;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((r) => r?.status !== false && (r?.image || r?.title))
      .sort((a, b) => (Number(a?.priority) || 0) - (Number(b?.priority) || 0));
  }, [sectionData?.metadata_1]);

  const status = isCmsShowFlagOn(sectionData?.status, true);
  const applyNowShow = isCmsShowFlagOn(sectionData?.apply_now_show, true);
  const applyNowIsCta = isCmsShowFlagOn(sectionData?.apply_now_is_cta, true);

  const handleCtaClick = useCallback(() => {
    ctaClick({
      section: "banner_5004",
      component: "marketing_banner_cta",
      label: apply_now_text?.trim() || "cta",
      step: "1",
      entity: "banner_5004",
      parentEntity: "banner_5004",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [apply_now_text, dispatch]);

  if (!status) return null;

  /* An unrecognised stored value (an older row, a hand-edited one) falls back to
     v1's more common layout rather than rendering nothing positioned. */
  const layout: LayoutVariant =
    backvariant === "variant_2" ? "variant_2" : "variant_1";
  const isRight = layout === "variant_2";
  /* The hero is the placement that carries accreditation cards; the marketing
     bands never do. That is the whole difference in type scale below. */
  const isHero = accreditations.length > 0;

  /* An explicit height always wins. Left unset, a hero falls back to the
     reference's viewport-relative heights and a band to its fixed default. */
  const explicitHeight = Number(hero_section_height) || 0;
  const heroHeight = explicitHeight
    ? `${explicitHeight}px`
    : `${DEFAULT_HEIGHT}px`;

  /* THE HERO'S BOX MUST NOT DEPEND ON ITS CONTENT.
     v1 sizes the hero from the art itself -- an <img> with a definite
     `height:60vh` / `75vh` -- and lays the copy over it absolutely. Painting the
     art as a background on the copy's own box (what this component does) loses
     that unless the box is given a definite height: a bare `min-height` lets the
     copy push the box taller, so removing the CTA visibly shrank the image.
     The marketing bands are deliberately NOT changed -- v1 leaves those
     content-driven at one breakpoint each (`md:h-auto` on the contact band, no
     mobile height on the EMI band). */
  const heroSizeClass = explicitHeight
    ? "h-[var(--kuk-mb-h)]"
    : `${HERO_HEIGHT_MOBILE_CLASS} ${HERO_HEIGHT_DESKTOP_CLASS}`;

  /* v1 pins a white lead-form card to the hero's right and hides it below its
     `md` (768px = this app's `tab`), leaving phones the CTA + lead-drop popup.
     Either CMS flag turns it on; both default off, so every page that sets
     neither renders exactly as before. Only the hero carries one. */
  const showLeadForm =
    isHero &&
    (isCmsShowFlagOn(show_lead_form, false) ||
      isCmsShowFlagOn(enable_lead_form, false));

  /* Resolve the form the same way Banner 9 / 10 do: look the university's
     selected variant up in the shared registry rather than importing one form
     directly, so whichever variant an editor picks in University Settings is
     the one that renders here. Forms self-hydrate from the leaddropform slice
     and take no props (only variant 6 is passed a row, which this hero does
     not use). */
  const leaddropFormVariant = useSelector(
    (state: RootState) => state.leaddropform.variant,
  );
  /* The card is only worth painting once the form behind it has something to
     render: every variant bails with `if (!formData) return null`, so gating on
     the row too is what stops a configured-but-unpopulated page showing an
     empty white box over the art. Same selector Banner 10 uses. */
  const leadDropFormRow = useSelector((state: RootState) => {
    const blob = state.leaddropform.lead_drop_form as
      | { formData?: Record<string, unknown> }
      | null
      | undefined;
    return blob?.formData ?? null;
  });
  const LeaddropFormComponent = useMemo(
    () =>
      leaddropFormVariant
        ? LEAD_DROP_FORM_SECTION_VARIANTS[leaddropFormVariant] ?? null
        : null,
    [leaddropFormVariant],
  );
  /* Gate on the measured width instead of `hidden tab:flex` so the form is not
     mounted at all on phones -- v1 hides the card below 768px and leaves the
     CTA + lead-drop popup to do the job. `enabled` keeps the hook from
     resolving (and re-rendering this subtree) on the marketing bands, which
     never carry a form. useIsMobile returns null until measured, so the
     `=== false` test also keeps the first client render matching the server. */
  const isMobile = useIsMobile(768, showLeadForm);

  const desktopImage = image ? toCssUrl(image) : "none";
  const mobileImage = mobile_image ? toCssUrl(mobile_image) : desktopImage;

  /* Both gradient stops or neither — a single stop is not a gradient, and
     falling back keeps a half-configured row rendering a valid button. */
  const useGradient = Boolean(gradient_color_3 && gradient_color_4);

  const ctaClassName = cn(
    "inline-flex h-10 items-center justify-center whitespace-nowrap rounded-md",
    "border px-6 text-sm font-medium shadow transition-colors tab:text-base",
  );
  const ctaStyle: CSSProperties = useGradient
    ? {
        backgroundImage: `linear-gradient(90deg, ${gradient_color_3} 0%, ${gradient_color_4} 100%)`,
        borderColor: "transparent",
        color: cta_text_color,
      }
    : {
        backgroundColor: apply_now_color,
        borderColor: apply_now_border_color,
        color: cta_text_color,
      };

  return (
    <section
      /* v1 paints the image with a CSS `background-image`, not a `next/image`,
         so the focal point can be expressed as a background position. Kept as-is
         — the art is decorative and carries no content of its own. */
      className={cn(
        "relative w-full bg-[image:var(--kuk-mb-bg-mobile)] bg-cover bg-no-repeat",
        "tab:bg-[image:var(--kuk-mb-bg)]",
        isHero ? heroSizeClass : "tab:h-[var(--kuk-mb-h)]",
        /* The lead card is a definite 520px inside a py-20 wrapper, so it needs
           680px of hero to sit inside. A 75vh hero is shorter than that on any
           window under ~907px tall, and the card then spills over the marquee
           above and the band below. The floor applies only from `tab` up, where
           the card actually renders, so the hero's height stays content-independent
           on phones. */
        showLeadForm && "tab:min-h-[680px]",
      )}
      style={
        {
          /* Bands only: the hero's height is definite, so a floor would be
             either redundant or a way for copy to grow it again. */
          ...(isHero ? null : { minHeight: `${DEFAULT_HEIGHT}px` }),
          backgroundColor: background_color,
          backgroundPosition: isHero ? HERO_FOCAL_POINT : FOCAL_POINT[layout],
          "--kuk-mb-bg": desktopImage,
          "--kuk-mb-bg-mobile": mobileImage,
          "--kuk-mb-h": heroHeight,
        } as CSSProperties
      }
      /* v1 gives the block no accessible name; the alt text is exposed the same
         way Banner 3001 does it, so an editor's alt copy is not simply lost. */
      aria-label={image_alt || undefined}
    >
      {/* The reference hero lays black at 30% over the photograph so the white
          copy stays readable. Only painted when a colour is set, so the
          marketing bands — which set none — are pixel-identical to before. */}
      {bleed_background_color ? (
        <div
          aria-hidden
          className="absolute inset-0 size-full"
          style={{ backgroundColor: bleed_background_color }}
        />
      ) : null}

      <div
        className={cn(
          /* Absolute on the hero so the copy cannot size it (v1 overlays its
             copy on the image the same way); in flow on the bands, which v1
             does let their copy size. */
          isHero
            ? "absolute inset-0 z-10"
            : "relative z-10 size-full min-h-[inherit]",
          /* Reserve the right 40% for the lead card so the two never overlap --
             v1 gives the form panel `md:w-[40%]`. Deliberately keyed to the CMS
             flag rather than to whether the card actually painted: the card is
             gated on client-only state (measured width, hydrated form row), so
             keying the copy to that too would move it on every load. Reserving
             from the server render costs a misconfigured page some unused space
             and buys every correct page zero layout shift. */
          showLeadForm && "tab:right-[40%]",
          "mx-auto flex flex-col gap-5 py-10",
          "tab:max-w-[1120px]",
          /* HERO INDENT. The reference does not centre its hero copy — it pins the
             block with `!left-5 md:!left-36 lg:left-44` and adds `md:px-5` inside,
             so the copy starts 40px / 164px / 196px from the left edge. Ported as
             left padding, because this block is already `inset-0`. The 1024px stop
             is written raw: it is the reference's own `lg`, and this app remaps the
             named `lg` to 1430px, which would move the indent 400px away from where
             the reference puts it. The bands keep their symmetric padding — only the
             hero is pinned this way. */
          isHero
            ? "pl-10 pr-10 tab:pl-[164px] tab:pr-20 min-[1024px]:pl-[196px]"
            : "px-10 tab:px-20",
          isRight
            ? "items-end justify-end text-right"
            : "items-start justify-center text-left",
        )}
      >
        {title ? (
          /* The reference hero's title is its `text-5xl` h1; the marketing bands
             are `text-[32px]` h2s. The badge row only exists on the hero, so it
             is what distinguishes the two roles — no extra field needed. */
          isHero ? (
            <h1
              className="text-3xl font-bold tab:text-5xl"
              style={{ color: title_color }}
            >
              {title}
            </h1>
          ) : (
            <h2
              className={cn(
                "text-2xl font-bold tab:text-[32px]",
                /* v1 caps the right-aligned copy so it breaks onto two lines the
                   way the reference does; the left-aligned block runs full width. */
                isRight && "max-w-[320px]",
              )}
              style={{ color: title_color }}
            >
              {title}
            </h2>
          )
        ) : null}

        {description ? (
          /* v1 pulls this up under the heading with a negative margin to defeat
             its own `gap-10`. Reproduced here as a smaller gap instead — same
             spacing on screen, without a margin that fights the layout. */
          <p
            className={cn(
              "-mt-3 text-base",
              /* Reference hero: `text-md font-light` under the title, with an
                 explicit `!mb-[30px]` before the badge row. That sits on a
                 `space-y-2` (8px) base there, so the gap it wants is 38px; this
                 block's base gap is `gap-5` (20px), leaving 18px to add. Without
                 it the badges ride up under the copy. */
              isHero && "mb-[18px] font-light",
              isRight && "max-w-[320px]",
            )}
            style={{ color: description_color }}
          >
            {description}
          </p>
        ) : null}

        {/* Accreditation cards — hero only. The reference stacks them below `lg`
            and lays them in a row above it. */}
        {accreditations.length > 0 ? (
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
            {accreditations.map((item, i) => (
              <div
                key={item.id ?? i}
                className="flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-2 py-1 shadow tab:p-2"
                style={{ backgroundColor: horizontal_stripe_color }}
              >
                {item.image ? (
                  <Image
                    src={item.image}
                    alt={item.alt_image || item.title || ""}
                    width={25}
                    height={25}
                    className="w-auto"
                    priority
                    loading="eager"
                    quality={85}
                  />
                ) : null}
                {item.title ? (
                  <p
                    className="text-xs tab:text-base"
                    style={{ color: card_title_color }}
                  >
                    {item.title}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {applyNowShow && apply_now_text ? (
          applyNowIsCta || !apply_now_link ? (
            <button
              type="button"
              onClick={handleCtaClick}
              className={ctaClassName}
              style={ctaStyle}
            >
              {apply_now_text}
            </button>
          ) : (
            <Link
              href={normalizeHref(apply_now_link)}
              className={ctaClassName}
              style={ctaStyle}
            >
              {apply_now_text}
            </Link>
          )
        ) : null}
      </div>

      {/* v1's lead-form panel: pinned to the hero's right, vertically centred,
          and hidden below `md` (= this app's `tab`) where the CTA and the
          lead-drop popup take over. The form self-hydrates from the leaddropform
          slice and renders nothing until an editor has configured form 5018. */}
      {showLeadForm && isMobile === false && LeaddropFormComponent && leadDropFormRow ? (
        <div className="absolute right-0 top-0 z-20 flex h-full w-[40%] flex-col items-center justify-center px-20 py-20">
          <div className="min-h-[520px] space-y-5 rounded-xl border border-slate-200 bg-white p-10 shadow">
            <LeaddropFormComponent />
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default Banner5004;
